import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const heroImages = [
  {
    src: '/images/hero/hero-1.png',
    topLabel: 'Front Desk Staff',
    bottomLabel: 'Program Coordinator',
  },
  {
    src: '/images/hero/hero-2.png',
    topLabel: 'Referral Coordinators',
    bottomLabel: 'Case Managers',
  },
  {
    src: '/images/hero/hero-3.png',
    topLabel: 'Communications Staff',
    bottomLabel: 'Clients',
  },
  {
    src: '/images/hero/hero-4.png',
    topLabel: 'Outreach Teams',
    bottomLabel: 'Training Staff',
  },
];

export default function Hero() {
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative isolate overflow-hidden bg-hero-gradient">
      {/* Decorative background elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/3 w-[800px] h-[800px] rounded-full bg-cleona-200/20 blur-3xl" />
      </div>

      <div className="mx-auto max-w-7xl px-6 pt-32 pb-24 sm:pt-40 lg:flex lg:items-center lg:gap-x-10 lg:px-8 lg:pt-40 lg:pb-32">
        {/* Left content */}
        <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl lg:flex-shrink-0">
          {/* Tag pill */}
          <div className="inline-flex items-center gap-3 rounded-pill bg-cleona-500 px-4 py-2 mb-8">
            <span className="text-white font-manrope font-semibold text-base">Build Your SmartAssistant&trade;</span>
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white">
              <svg className="w-4 h-4 text-cleona-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-11.25a.75.75 0 00-1.5 0v2.5h-2.5a.75.75 0 000 1.5h2.5v2.5a.75.75 0 001.5 0v-2.5h2.5a.75.75 0 000-1.5h-2.5v-2.5z" clipRule="evenodd" />
              </svg>
            </span>
          </div>

          {/* Main headline */}
          <h1 className="font-jakarta text-5xl font-bold tracking-tight text-cleona-600 sm:text-6xl lg:text-7xl leading-tight">
            <span className="italic">Easily Build</span> Your SmartAssistant&trade; From Your Resources
          </h1>

          {/* Subheadline */}
          <p className="mt-6 text-lg leading-8 text-gray-900 font-manrope font-semibold max-w-lg">
            Upload your documents and links, CLEONA instantly turns them into a conversational assistant that gives people clear answers when they need them. No coding. No complexity. Just instant clarity.
          </p>

          {/* CTA Button */}
          <div className="mt-10">
            <Link
              to="/admin/signup"
              className="inline-flex items-center gap-2 rounded-pill bg-white px-6 py-3 text-base font-bold text-cleona-600 shadow-lg hover:shadow-xl transition-all hover:scale-105 font-manrope"
            >
              Start Building
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Right content - Hero image with orbits */}
        <div className="mx-auto mt-16 flex max-w-2xl sm:mt-24 lg:ml-10 lg:mr-0 lg:mt-0 lg:max-w-none lg:flex-none xl:ml-32">
          <div className="relative w-[400px] h-[400px] lg:w-[500px] lg:h-[500px]">
            {/* Orbit lines */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 500 500">
              <ellipse cx="250" cy="250" rx="240" ry="100" fill="none" stroke="#007073" strokeWidth="1" transform="rotate(-20 250 250)" opacity="0.3" />
              <ellipse cx="250" cy="250" rx="200" ry="80" fill="none" stroke="#007073" strokeWidth="1" transform="rotate(-20 250 250)" opacity="0.4" />
              <ellipse cx="250" cy="250" rx="160" ry="60" fill="none" stroke="#007073" strokeWidth="1" transform="rotate(-20 250 250)" opacity="0.5" />
            </svg>

            {/* Main circular image */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 lg:w-80 lg:h-80 rounded-full overflow-hidden border-4 border-white shadow-2xl">
              {heroImages.map((image, index) => (
                <img
                  key={index}
                  src={image.src}
                  alt={image.topLabel}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                    index === currentImage ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}
            </div>

            {/* Floating label - top right */}
            <div className="absolute top-8 right-0 lg:top-12 lg:right-4">
              <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-lg">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-cleona-600">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </span>
                <span className="text-sm font-semibold text-gray-900 font-manrope">
                  {heroImages[currentImage].topLabel}
                </span>
              </div>
            </div>

            {/* Floating label - bottom left */}
            <div className="absolute bottom-16 left-0 lg:bottom-20 lg:left-4">
              <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-lg">
                <span className="text-sm font-semibold text-gray-900 font-manrope">{heroImages[currentImage].bottomLabel}</span>
                <div className="flex gap-0.5">
                  <div className="w-1 h-3 bg-cleona-600 rounded-full" />
                  <div className="w-1 h-4 bg-cleona-600 rounded-full" />
                  <div className="w-1 h-2 bg-cleona-600 rounded-full" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
